import { Award, GitBranch, Heart, Layers, Radio, Sprout } from "lucide-react";
import type { Profile } from "@shared/api-types.js";
import { BADGES } from "@shared/badges.js";
import { cn } from "@web-shared/lib/utils";
import { Avatar } from "./Avatar";

/** How far through the current level, 0–1. */
export function levelProgress(game: Profile["game"]): number {
  const span = game.nextLevel - game.levelFloor;
  return span > 0 ? Math.min(1, Math.max(0, (game.xp - game.levelFloor) / span)) : 0;
}

/** A profile picture wrapped in its level: the ring fills toward the next one. */
export function LevelAvatar({ profile, live }: { profile: Profile; live: boolean }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  const done = levelProgress(profile.game);
  return (
    <div className="relative size-24 shrink-0" title={`Level ${profile.game.level} · ${profile.game.xp} XP`}>
      <svg viewBox="0 0 100 100" className="absolute inset-0 size-full -rotate-90" aria-hidden>
        <circle cx="50" cy="50" r={r} fill="none" stroke="var(--border)" strokeWidth="4" />
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={`${done * c} ${c}`}
        />
      </svg>
      <Avatar name={profile.username} url={profile.avatarUrl} live={live} className="absolute inset-2 size-20 text-xl" />
      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-full border-2 border-card bg-primary px-2 text-[10px] font-bold leading-4 text-primary-foreground">
        LV {profile.game.level}
      </span>
    </div>
  );
}

/** "Level 3 · 420 / 600 XP" with a bar. */
export function LevelBar({ profile }: { profile: Profile }) {
  const g = profile.game;
  return (
    <div className="max-w-xs space-y-1">
      <p className="flex justify-between text-[11px] text-muted-foreground">
        <span>Level {g.level}</span>
        <span className="tabular">
          {g.xp} / {g.nextLevel} XP
        </span>
      </p>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary" style={{ width: `${levelProgress(g) * 100}%` }} />
      </div>
    </div>
  );
}

const ICONS: Record<string, React.ReactNode> = {
  "first-seed": <Sprout className="size-4" />,
  "first-remix": <GitBranch className="size-4" />,
  "remixed-10": <Layers className="size-4" />,
  "five-generations": <Award className="size-4" />,
  "jam-regular": <Radio className="size-4" />,
  "crowd-favourite": <Heart className="size-4" />,
};

/** Every badge there is: the ones held lit, the rest waiting. */
export function BadgeShelf({ profile }: { profile: Profile }) {
  const held = new Set(profile.game.badges);
  return (
    <ul className="flex flex-wrap gap-2" aria-label="Badges">
      {BADGES.map((b) => {
        const has = held.has(b.id);
        return (
          <li
            key={b.id}
            title={`${b.label} — ${b.description}${has ? "" : " (not yet)"}`}
            className={cn(
              // `relative` so the screen-reader span below is positioned
              // against its own pill. Absolutely positioned with no positioned
              // ancestor it hung off the initial containing block instead —
              // outside the badge shelf's scroller, which gave the profile page
              // 8px of sideways scroll on a phone.
              "relative flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px]",
              has ? "border-primary/50 bg-primary/10 text-primary" : "border-dashed border-border text-muted-foreground/60",
            )}
          >
            {ICONS[b.id] ?? <Award className="size-4" />}
            <span className={cn(!has && "hidden sm:inline")}>{b.label}</span>
            <span className="sr-only">{has ? "earned" : "not earned yet"}</span>
          </li>
        );
      })}
    </ul>
  );
}
